import React, { useState, useEffect, useCallback, useRef } from 'react';
import { TrendingUp, Brain, Package, AlertTriangle, Loader2, Send, RefreshCw, ArrowRightLeft, BarChart3, ShoppingCart, Factory, Truck, MessageSquare, Plus, Clock, ClipboardList, Download, Trash2, Search } from 'lucide-react';
import { forecastAPI, branchAPI, productAPI } from '../../services/api';
import { filterBranchesForUser } from '../../utils/branchScope';
import * as XLSX from 'xlsx';

const urgencyColors = {
  'high': 'bg-red-100 text-red-700 border-red-200',
  'medium': 'bg-amber-100 text-amber-700 border-amber-200',
  'low': 'bg-green-100 text-green-700 border-green-200',
};

const categoryIcons = {
  'demand': TrendingUp,
  'procurement': ShoppingCart,
  'stock': Package,
  'production': Factory,
  'financial': BarChart3,
  'logistics': Truck,
};

const ForecastModule = () => {
  const [activeTab, setActiveTab] = useState('ai');
  const [loading, setLoading] = useState(false);
  const [erpData, setErpData] = useState(null);
  const [query, setQuery] = useState('');
  const [analysisContext, setAnalysisContext] = useState('general');
  const [convForm, setConvForm] = useState({ product_name: '', value: 1, from_unit: 'pcs', to_unit: 'kg' });
  const [convResult, setConvResult] = useState(null);
  const [convLoading, setConvLoading] = useState(false);

  // Conversation history
  const [sessionId, setSessionId] = useState(null);
  const [chatHistory, setChatHistory] = useState([]);
  const chatEndRef = useRef(null);

  // Material Planning (MRP)
  const [mpBranches, setMpBranches] = useState([]);
  const [mpBranch, setMpBranch] = useState('');
  const [mpTargets, setMpTargets] = useState([]);
  const [mpResult, setMpResult] = useState(null);
  const [mpLoading, setMpLoading] = useState(false);
  const [mpSearch, setMpSearch] = useState('');
  const [mpSearchResults, setMpSearchResults] = useState([]);
  const [mpSearching, setMpSearching] = useState(false);
  const mpSearchTimer = useRef(null);

  const loadMpBranches = useCallback(async () => {
    try {
      const data = await branchAPI.getAll();
      const list = Array.isArray(data) ? data : (data.items || []);
      const prodBranches = list.filter(b => (b.type || '').toLowerCase().includes('production') || (b.is_production));
      setMpBranches(prodBranches.length ? prodBranches : list);
    } catch (e) { /* noop */ }
  }, []);
  useEffect(() => { if (activeTab === 'mrp' && mpBranches.length === 0) loadMpBranches(); }, [activeTab, mpBranches.length, loadMpBranches]);

  const handleMpSearch = (val) => {
    setMpSearch(val);
    if (mpSearchTimer.current) clearTimeout(mpSearchTimer.current);
    if (!val || val.trim().length < 2) { setMpSearchResults([]); return; }
    mpSearchTimer.current = setTimeout(async () => {
      try {
        setMpSearching(true);
        const res = await productAPI.getAll({ search: val.trim(), page_size: 15 });
        setMpSearchResults(Array.isArray(res) ? res : (res?.items || []));
      } catch (e) { setMpSearchResults([]); }
      finally { setMpSearching(false); }
    }, 300);
  };

  const addMpTarget = (product) => {
    if (mpTargets.some(t => t.product_id === product.id)) { setMpSearch(''); setMpSearchResults([]); return; }
    setMpTargets(prev => [...prev, { product_id: product.id, product_name: product.name, unit: product.unit || '', quantity: '' }]);
    setMpSearch(''); setMpSearchResults([]);
  };
  const updateMpQty = (idx, val) => setMpTargets(prev => prev.map((t, i) => i === idx ? { ...t, quantity: val } : t));
  const removeMpTarget = (idx) => setMpTargets(prev => prev.filter((_, i) => i !== idx));

  const runMaterialPlanning = async () => {
    const targets = mpTargets.filter(t => Number(t.quantity) > 0).map(t => ({ product_id: t.product_id, product_name: t.product_name, quantity: Number(t.quantity) }));
    if (targets.length === 0) return;
    setMpLoading(true); setMpResult(null);
    try {
      const res = await forecastAPI.materialPlanning(targets, mpBranch || null);
      setMpResult(res);
    } catch (e) {
      setMpResult({ error: e.response?.data?.detail || 'Material planning failed' });
    } finally { setMpLoading(false); }
  };

  const exportMpCsv = () => {
    if (!mpResult?.rows?.length) return;
    const wb = XLSX.utils.book_new();
    const branchName = mpBranches.find(b => b.id === mpResult.branch_id)?.name || 'All Branches';

    // Sheet 1 — Raw Material Requirement
    const headers = ['Raw material', 'Unit', 'Required for Production', 'In Stock', 'In Planned', 'In Progress', 'Ordered', 'Net Shortfall'];
    const rows = mpResult.rows.map(r => [r.raw_material, r.unit, r.required, r.in_stock, r.in_planned, r.in_progress, r.ordered, r.net_shortfall]);
    const ws1 = XLSX.utils.aoa_to_sheet([
      [`Material Requirement Plan — ${branchName}`],
      [`Generated: ${new Date().toLocaleString()}`],
      [],
      headers,
      ...rows,
    ]);
    ws1['!cols'] = [{ wch: 34 }, { wch: 8 }, { wch: 20 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 14 }];
    XLSX.utils.book_append_sheet(wb, ws1, 'Raw Material Requirement');

    // Sheet 2 — Manufacturing Targets (the input)
    const tgtRows = (mpResult.included_products || []).map(p => [p.product, p.quantity, p.unit]);
    if (tgtRows.length) {
      const ws2 = XLSX.utils.aoa_to_sheet([['Product', 'Manufacturing Target', 'Unit'], ...tgtRows]);
      ws2['!cols'] = [{ wch: 34 }, { wch: 20 }, { wch: 8 }];
      XLSX.utils.book_append_sheet(wb, ws2, 'Manufacturing Targets');
    }

    // Sheet 3 — Excluded (no BOM), if any
    if (mpResult.products_without_bom?.length) {
      const exRows = mpResult.products_without_bom.map(p => [p.product, p.reason]);
      const ws3 = XLSX.utils.aoa_to_sheet([['Product (Not Included)', 'Reason'], ...exRows]);
      ws3['!cols'] = [{ wch: 34 }, { wch: 30 }];
      XLSX.utils.book_append_sheet(wb, ws3, 'Not Included');
    }

    XLSX.writeFile(wb, `material-planning-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };


  const fetchERP = useCallback(async () => {
    try {
      const data = await forecastAPI.getERPSummary();
      setErpData(data);
    } catch (err) { console.error('ERP summary error:', err); }
  }, []);

  useEffect(() => { fetchERP(); }, [fetchERP]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatHistory]);

  const quickQueries = [
    { label: 'Demand Forecast', query: 'Analyze demand trends for all product categories. What are the top-demand products and what should we stock up on for the next 3 months?', ctx: 'demand' },
    { label: 'RM Planning', query: 'Analyze raw material stock levels vs production requirements. Which materials are running low? What should we order immediately?', ctx: 'rawmaterial' },
    { label: 'Procurement Timing', query: 'Based on pending indents, production orders, and current stock, what purchase orders should we create this week?', ctx: 'procurement' },
    { label: 'Production Capacity', query: 'Review production orders and stock. Do we have enough raw materials? What bottlenecks exist?', ctx: 'demand' },
    { label: 'Full Business Review', query: 'Complete business health check: stock levels, pending orders, production status, dispatch status, and key risks. Prioritize action items.', ctx: 'general' },
  ];

  const handleAnalysis = async (q = query, ctx = analysisContext) => {
    if (!q.trim()) return;
    setLoading(true);

    // Add user message to history
    setChatHistory(prev => [...prev, { role: 'user', text: q, context: ctx, timestamp: new Date() }]);
    setQuery('');

    try {
      const result = await forecastAPI.getAIAnalysis(q, ctx, sessionId);
      if (result.session_id) setSessionId(result.session_id);
      setChatHistory(prev => [...prev, {
        role: 'ai',
        analysis: result.analysis,
        raw: result.raw,
        isFollowup: result.is_followup,
        timestamp: new Date(),
      }]);
    } catch (err) {
      console.error('AI analysis error:', err);
      setChatHistory(prev => [...prev, {
        role: 'error',
        text: err.response?.data?.detail || 'Analysis failed. Please try again.',
        timestamp: new Date(),
      }]);
    } finally { setLoading(false); }
  };

  const startNewSession = () => {
    setSessionId(null);
    setChatHistory([]);
    setQuery('');
  };

  const handleConvert = async () => {
    if (!convForm.product_name || !convForm.value) return;
    setConvLoading(true);
    try {
      const result = await forecastAPI.convertUnits(convForm);
      setConvResult(result);
    } catch (err) {
      setConvResult({ error: err.response?.data?.detail || 'Conversion failed' });
    } finally { setConvLoading(false); }
  };

  // Render a single AI analysis result
  const renderAnalysis = (analysis) => {
    if (!analysis) return null;
    return (
      <div className="space-y-3">
        {analysis.summary && (
          <div className="bg-gradient-to-r from-blue-50 to-white rounded-xl border border-blue-100 p-3">
            <div className="flex items-center gap-2 mb-1.5"><Brain className="w-3.5 h-3.5" style={{ color: '#24549c' }} /><h3 className="text-[11px] font-bold" style={{ color: '#24549c' }}>Executive Summary</h3></div>
            <p className="text-xs text-gray-700 leading-relaxed">{analysis.summary}</p>
          </div>
        )}

        {analysis.insights?.length > 0 && (
          <div>
            <h3 className="text-[11px] font-bold text-gray-900 mb-2">Key Insights</h3>
            <div className="space-y-1.5">
              {analysis.insights.map((insight, i) => {
                const Icon = categoryIcons[insight.category] || Brain;
                return (
                  <div key={i} data-testid={`insight-${i}`} className={`flex items-start gap-2.5 p-2.5 rounded-lg border ${urgencyColors[insight.urgency] || 'bg-gray-50 border-gray-200'}`}>
                    <Icon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] font-bold">{insight.title}</p>
                      <p className="text-[10px] mt-0.5 opacity-80">{insight.description}</p>
                    </div>
                    <span className={`px-1.5 py-0.5 text-[9px] font-bold rounded-full flex-shrink-0 ${urgencyColors[insight.urgency] || ''}`}>{insight.urgency?.toUpperCase()}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {analysis.demand_forecast?.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <div className="px-3 py-2 border-b border-gray-100 flex items-center gap-2"><TrendingUp className="w-3.5 h-3.5" style={{ color: '#24549c' }} /><h3 className="text-[11px] font-bold text-gray-900">Demand Forecast</h3></div>
            <div className="overflow-x-auto">
              <table className="w-full text-[11px]">
                <thead><tr className="bg-gray-50">{['Product', 'Current/mo', 'Forecast', 'Trend', 'Confidence'].map(h => <th key={h} className="text-left px-3 py-1.5 text-[10px] font-semibold text-gray-500">{h}</th>)}</tr></thead>
                <tbody>{analysis.demand_forecast.map((d, i) => (
                  <tr key={i} className="border-t border-gray-50">
                    <td className="px-3 py-1.5 font-semibold">{d.product}</td>
                    <td className="px-3 py-1.5">{d.current_monthly}</td>
                    <td className="px-3 py-1.5 font-bold" style={{ color: '#24549c' }}>{d.forecasted_next_month}</td>
                    <td className="px-3 py-1.5"><span className={d.trend === 'up' ? 'text-green-600' : d.trend === 'down' ? 'text-red-600' : 'text-gray-500'}>{d.trend === 'up' ? 'Trending Up' : d.trend === 'down' ? 'Trending Down' : 'Stable'}</span></td>
                    <td className="px-3 py-1.5"><span className={`inline-flex px-1.5 py-0.5 text-[9px] font-bold rounded-full ${d.confidence === 'high' ? 'bg-green-100 text-green-700' : d.confidence === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>{d.confidence}</span></td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </div>
        )}

        {analysis.procurement_recommendations?.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <div className="px-3 py-2 border-b border-gray-100 flex items-center gap-2"><ShoppingCart className="w-3.5 h-3.5" style={{ color: '#24549c' }} /><h3 className="text-[11px] font-bold text-gray-900">Procurement Recommendations</h3></div>
            <div className="overflow-x-auto">
              <table className="w-full text-[11px]">
                <thead><tr className="bg-gray-50">{['Material', 'Action', 'Qty', 'Urgency', 'Est. Cost', 'Reason'].map(h => <th key={h} className="text-left px-3 py-1.5 text-[10px] font-semibold text-gray-500">{h}</th>)}</tr></thead>
                <tbody>{analysis.procurement_recommendations.map((r, i) => (
                  <tr key={i} className="border-t border-gray-50">
                    <td className="px-3 py-1.5 font-semibold">{r.material}</td>
                    <td className="px-3 py-1.5">{r.action}</td>
                    <td className="px-3 py-1.5 font-mono">{r.quantity}</td>
                    <td className="px-3 py-1.5"><span className={`inline-flex px-1.5 py-0.5 text-[9px] font-bold rounded-full ${urgencyColors[r.urgency] || 'bg-gray-100'}`}>{r.urgency}</span></td>
                    <td className="px-3 py-1.5 font-semibold">{typeof r.estimated_cost === 'number' ? `INR ${r.estimated_cost.toLocaleString()}` : r.estimated_cost || '-'}</td>
                    <td className="px-3 py-1.5 text-gray-500 max-w-[180px] truncate">{r.reason}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </div>
        )}

        {analysis.stock_alerts?.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-2"><AlertTriangle className="w-3.5 h-3.5" style={{ color: '#E65100' }} /><h3 className="text-[11px] font-bold text-gray-900">Stock Alerts</h3></div>
            <div className="grid md:grid-cols-2 gap-1.5">
              {analysis.stock_alerts.map((a, i) => (
                <div key={i} className={`p-2.5 rounded-lg border ${a.status === 'critical' ? 'bg-red-50 border-red-200' : a.status === 'low' ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
                  <div className="flex justify-between items-start">
                    <p className="text-[11px] font-semibold">{a.product}</p>
                    <span className={`px-1.5 py-0.5 text-[9px] font-bold rounded-full ${a.status === 'critical' ? 'bg-red-100 text-red-700' : a.status === 'low' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>{a.status}</span>
                  </div>
                  <p className="text-[10px] mt-0.5">Qty: {a.current_qty} | {a.action}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {analysis.production_suggestions?.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-2"><Factory className="w-3.5 h-3.5" style={{ color: '#24549c' }} /><h3 className="text-[11px] font-bold text-gray-900">Production Suggestions</h3></div>
            <div className="space-y-1.5">
              {analysis.production_suggestions.map((s, i) => (
                <div key={i} className="flex items-start gap-2.5 p-2.5 bg-gray-50 rounded-lg">
                  <span className={`mt-0.5 px-1.5 py-0.5 text-[9px] font-bold rounded-full ${urgencyColors[s.priority] || 'bg-gray-100'}`}>{s.priority}</span>
                  <div><p className="text-[11px] font-semibold">{s.suggestion}</p><p className="text-[10px] text-gray-500 mt-0.5">{s.impact}</p></div>
                </div>
              ))}
            </div>
          </div>
        )}

        {analysis.conversion_tips?.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-2"><ArrowRightLeft className="w-3.5 h-3.5" style={{ color: '#24549c' }} /><h3 className="text-[11px] font-bold text-gray-900">Unit Conversion Notes</h3></div>
            <div className="space-y-1">
              {analysis.conversion_tips.map((t, i) => (
                <div key={i} className="text-[11px] p-2 bg-blue-50 rounded-lg border border-blue-100">
                  <span className="font-semibold" style={{ color: '#24549c' }}>{t.context}:</span> {t.tip}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="p-5 lg:p-6" data-testid="forecast-module">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-5 gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>AI Forecast & Analytics</h1>
          <p className="text-xs text-gray-500 mt-0.5">AI-powered demand forecasting, RM planning & unit conversion</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 rounded-lg border border-blue-100">
            <Brain className="w-3.5 h-3.5" style={{ color: '#24549c' }} />
            <span className="text-[11px] font-semibold" style={{ color: '#24549c' }}>GPT-4.1 Mini</span>
          </div>
          <button onClick={fetchERP} className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50" title="Refresh Data">
            <RefreshCw className="w-3.5 h-3.5 text-gray-500" />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1.5 mb-5">
        {[
          { id: 'ai', label: 'AI Analysis', icon: Brain },
          { id: 'mrp', label: 'Material Planning', icon: ClipboardList },
          { id: 'converter', label: 'Unit Converter', icon: ArrowRightLeft },
          { id: 'dashboard', label: 'ERP Overview', icon: BarChart3 },
        ].map(t => (
          <button key={t.id} data-testid={`forecast-tab-${t.id}`} onClick={() => setActiveTab(t.id)} className={`flex items-center gap-1.5 px-4 py-2 text-[11px] font-medium rounded-full transition-all ${activeTab === t.id ? 'text-white' : 'text-gray-600 bg-gray-100 hover:bg-gray-200'}`} style={activeTab === t.id ? { backgroundColor: '#24549c' } : {}}>
            <t.icon className="w-3.5 h-3.5" />{t.label}
          </button>
        ))}
      </div>

      {/* ==================== AI ANALYSIS TAB ==================== */}
      {activeTab === 'ai' && (
        <div className="space-y-4">
          {/* Session info + quick queries */}
          <div className="flex flex-wrap items-center gap-2">
            {chatHistory.length > 0 && (
              <button onClick={startNewSession} data-testid="new-session-btn"
                className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium bg-white border border-gray-200 rounded-full hover:border-blue-300 hover:bg-blue-50 transition-all">
                <Plus className="w-3 h-3" />New Session
              </button>
            )}
            {chatHistory.length === 0 && quickQueries.map((q, i) => (
              <button key={i} data-testid={`quick-query-${i}`}
                onClick={() => { setAnalysisContext(q.ctx); handleAnalysis(q.query, q.ctx); }}
                className="px-3 py-1.5 text-[11px] font-medium bg-white border border-gray-200 rounded-full hover:border-blue-300 hover:bg-blue-50 transition-all">
                {q.label}
              </button>
            ))}
            {sessionId && chatHistory.length > 0 && (
              <span className="flex items-center gap-1 text-[10px] text-gray-400 ml-auto">
                <MessageSquare className="w-3 h-3" />Session active — follow-up questions reuse context
              </span>
            )}
          </div>

          {/* Chat History */}
          {chatHistory.length > 0 && (
            <div className="space-y-3 max-h-[calc(100vh-320px)] overflow-y-auto pr-1" data-testid="chat-history">
              {chatHistory.map((msg, idx) => (
                <div key={idx} data-testid={`chat-msg-${idx}`}>
                  {msg.role === 'user' && (
                    <div className="flex justify-end mb-1">
                      <div className="max-w-[80%] bg-blue-50 border border-blue-100 rounded-xl rounded-tr-sm p-3">
                        <div className="flex items-center gap-1.5 mb-1">
                          <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full" style={{ backgroundColor: '#24549c15', color: '#24549c' }}>{msg.context}</span>
                          <span className="text-[9px] text-gray-400">{msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                        <p className="text-xs text-gray-800">{msg.text}</p>
                      </div>
                    </div>
                  )}
                  {msg.role === 'ai' && (
                    <div className="flex justify-start">
                      <div className="max-w-[95%] bg-white border border-gray-100 rounded-xl rounded-tl-sm p-3 shadow-sm">
                        <div className="flex items-center gap-1.5 mb-2">
                          <Brain className="w-3.5 h-3.5" style={{ color: '#24549c' }} />
                          <span className="text-[10px] font-bold" style={{ color: '#24549c' }}>AI Analysis</span>
                          {msg.isFollowup && <span className="text-[9px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full">follow-up</span>}
                          <span className="text-[9px] text-gray-400">{msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                        {msg.analysis ? renderAnalysis(msg.analysis) : (
                          <div className="text-xs text-gray-700 whitespace-pre-wrap leading-relaxed">{msg.raw}</div>
                        )}
                      </div>
                    </div>
                  )}
                  {msg.role === 'error' && (
                    <div className="flex justify-start">
                      <div className="max-w-[80%] bg-red-50 border border-red-200 rounded-xl p-3">
                        <p className="text-xs text-red-700">{msg.text}</p>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" style={{ color: '#24549c' }} />
                      <span className="text-xs text-gray-500">
                        {sessionId ? 'Analyzing follow-up...' : 'Loading ERP data & analyzing...'}
                      </span>
                    </div>
                    {!sessionId && erpData && (
                      <p className="text-[10px] text-gray-400 mt-1 ml-6">Reviewing {erpData.summary?.total_pos || 0} POs, {erpData.summary?.total_stock_items || 0} stock items</p>
                    )}
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>
          )}

          {/* Empty state */}
          {chatHistory.length === 0 && !loading && (
            <div className="bg-white rounded-xl border border-gray-100 p-8 text-center">
              <Brain className="w-10 h-10 mx-auto mb-3 text-gray-300" />
              <p className="text-sm font-semibold text-gray-700">Ask anything about your business</p>
              <p className="text-xs text-gray-400 mt-1">Click a quick query above or type your own question below</p>
              {erpData && (
                <p className="text-[10px] text-gray-300 mt-3">Connected to {erpData.summary?.total_products || 0} products, {erpData.summary?.total_pos || 0} POs, {erpData.summary?.total_stock_items || 0} stock items</p>
              )}
            </div>
          )}

          {/* Initial loading (before any chat) */}
          {chatHistory.length === 0 && loading && (
            <div className="bg-white rounded-xl border border-gray-100 p-8 text-center">
              <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3" style={{ color: '#24549c' }} />
              <p className="text-sm font-semibold text-gray-700">Loading ERP data & analyzing...</p>
              <p className="text-xs text-gray-400 mt-1">First query loads context (~10-15s). Follow-ups will be faster.</p>
            </div>
          )}

          {/* Input */}
          <div className="bg-white rounded-xl border border-gray-200 p-3 sticky bottom-0">
            <div className="flex gap-2">
              <select value={analysisContext} onChange={e => setAnalysisContext(e.target.value)}
                className="px-3 py-2.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" data-testid="ai-context-select">
                <option value="general">General</option>
                <option value="demand">Demand</option>
                <option value="rawmaterial">Raw Material</option>
                <option value="procurement">Procurement</option>
                <option value="quotation">Quotation</option>
              </select>
              <input data-testid="ai-query-input" value={query} onChange={e => setQuery(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !loading && handleAnalysis()}
                placeholder={sessionId ? "Ask a follow-up question..." : "Ask AI anything about your business..."}
                className="flex-1 px-4 py-2.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
              <button data-testid="ai-query-submit" onClick={() => handleAnalysis()} disabled={loading || !query.trim()}
                className="flex items-center gap-1.5 px-4 py-2.5 text-xs font-semibold text-white rounded-lg hover:shadow-lg disabled:opacity-50 transition-all" style={{ backgroundColor: '#24549c' }}>
                {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                {loading ? 'Analyzing...' : 'Analyze'}
              </button>
            </div>
            {sessionId && <p className="text-[9px] text-gray-400 mt-1.5 ml-1"><Clock className="w-2.5 h-2.5 inline mr-0.5" />Session active — follow-up questions are faster (no data reload)</p>}
          </div>
        </div>
      )}

      {/* ==================== MATERIAL PLANNING (MRP) TAB ==================== */}
      {activeTab === 'mrp' && (
        <div className="space-y-4" data-testid="mrp-tab">
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
              <div>
                <h3 className="text-sm font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>Raw Material Requirement Planning</h3>
                <p className="text-xs text-gray-500 mt-0.5">Add finished goods + target qty. We explode each BOM to compute raw material needs.</p>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-[11px] font-medium text-gray-600">Production Branch:</label>
                <select data-testid="mrp-branch-select" value={mpBranch} onChange={e => setMpBranch(e.target.value)}
                  className="px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200">
                  <option value="">All Branches</option>
                  {filterBranchesForUser(mpBranches).map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
            </div>

            {/* Product search */}
            <div className="relative mb-3">
              <div className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2">
                <Search className="w-3.5 h-3.5 text-gray-400" />
                <input data-testid="mrp-product-search" value={mpSearch} onChange={e => handleMpSearch(e.target.value)}
                  placeholder="Search a finished good to add (min 2 chars)..."
                  className="flex-1 text-xs focus:outline-none" />
                {mpSearching && <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" />}
              </div>
              {mpSearchResults.length > 0 && (
                <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto" data-testid="mrp-search-results">
                  {mpSearchResults.map(p => (
                    <button key={p.id} data-testid={`mrp-search-opt-${p.id}`} onClick={() => addMpTarget(p)}
                      className="w-full text-left px-3 py-2 text-xs hover:bg-blue-50 border-b border-gray-50 last:border-0">
                      <span className="font-semibold text-gray-800">{p.name}</span>
                      <span className="text-gray-400 ml-2">{p.sku} · {p.category || ''} · {p.unit || ''}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Targets table */}
            {mpTargets.length > 0 ? (
              <div className="border border-gray-100 rounded-lg overflow-hidden mb-3">
                <table className="w-full text-xs">
                  <thead><tr className="bg-gray-50">
                    <th className="text-left px-3 py-2 text-[10px] font-semibold text-gray-500">Product</th>
                    <th className="text-left px-3 py-2 text-[10px] font-semibold text-gray-500 w-40">Manufacturing Target</th>
                    <th className="px-3 py-2 w-10"></th>
                  </tr></thead>
                  <tbody>
                    {mpTargets.map((t, i) => (
                      <tr key={t.product_id} className="border-t border-gray-50" data-testid={`mrp-target-row-${i}`}>
                        <td className="px-3 py-2 font-semibold text-gray-800">{t.product_name}</td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1.5">
                            <input data-testid={`mrp-target-qty-${i}`} type="number" min="0" step="any" value={t.quantity}
                              onChange={e => updateMpQty(i, e.target.value)}
                              className="w-24 px-2 py-1 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-200" />
                            <span className="text-[11px] text-gray-500">{t.unit}</span>
                          </div>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <button data-testid={`mrp-remove-${i}`} onClick={() => removeMpTarget(i)} className="p-1 rounded hover:bg-red-50">
                            <Trash2 className="w-3.5 h-3.5 text-red-500" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-6 text-xs text-gray-400 border border-dashed border-gray-200 rounded-lg mb-3">
                Search and add finished-good products above to plan their raw materials.
              </div>
            )}

            <button data-testid="mrp-calculate-btn" onClick={runMaterialPlanning}
              disabled={mpLoading || mpTargets.filter(t => Number(t.quantity) > 0).length === 0}
              className="flex items-center gap-1.5 px-5 py-2.5 text-xs font-semibold text-white rounded-lg hover:shadow-lg disabled:opacity-50 transition-all" style={{ backgroundColor: '#24549c' }}>
              {mpLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ClipboardList className="w-3.5 h-3.5" />}
              {mpLoading ? 'Calculating...' : 'Calculate Requirement'}
            </button>
          </div>

          {/* Result */}
          {mpResult?.error && (
            <div className="p-3 bg-red-50 border border-red-100 rounded-lg text-xs text-red-700">{mpResult.error}</div>
          )}
          {mpResult?.products_without_bom?.length > 0 && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="mrp-nobom-warning">
              <div className="flex items-center gap-2 mb-1"><AlertTriangle className="w-3.5 h-3.5 text-amber-600" /><span className="text-[11px] font-bold text-amber-800">Not included (no BOM/materials)</span></div>
              <p className="text-[11px] text-amber-700">{mpResult.products_without_bom.map(p => `${p.product} (${p.reason})`).join(', ')}. Add an Active BOM with materials to include these.</p>
            </div>
          )}
          {mpResult?.rows && (
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden" data-testid="mrp-result-table">
              <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                <h3 className="text-xs font-bold text-gray-900">Raw Material Requirement ({mpResult.rows.length} materials)</h3>
                {mpResult.rows.length > 0 && (
                  <button data-testid="mrp-export-btn" onClick={exportMpCsv} className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium border border-gray-200 rounded-lg hover:bg-gray-50">
                    <Download className="w-3 h-3" /> Export Excel
                  </button>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead><tr className="bg-gray-50">
                    {['Raw material', 'Required for Production', 'In Stock', 'In Planned', 'In Progress', 'Ordered', 'Net Shortfall'].map(h => (
                      <th key={h} className="text-left px-3 py-2 text-[10px] font-semibold text-gray-500 whitespace-nowrap">{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {mpResult.rows.length === 0 ? (
                      <tr><td colSpan={7} className="px-3 py-6 text-center text-gray-400">No raw-material requirement computed.</td></tr>
                    ) : mpResult.rows.map((r, i) => (
                      <tr key={r.sku} className="border-t border-gray-50" data-testid={`mrp-row-${i}`}>
                        <td className="px-3 py-2 font-semibold text-gray-800">{r.raw_material} <span className="text-gray-400 font-normal">{r.unit}</span></td>
                        <td className="px-3 py-2 font-bold" style={{ color: '#24549c' }}>{r.required.toLocaleString()}</td>
                        <td className="px-3 py-2 text-green-700">{r.in_stock.toLocaleString()}</td>
                        <td className="px-3 py-2 text-gray-600">{r.in_planned.toLocaleString()}</td>
                        <td className="px-3 py-2 text-gray-600">{r.in_progress.toLocaleString()}</td>
                        <td className="px-3 py-2 text-blue-600">{r.ordered.toLocaleString()}</td>
                        <td className="px-3 py-2 font-bold">
                          {r.net_shortfall > 0 ? (
                            <span className="inline-flex px-2 py-0.5 rounded-full bg-red-100 text-red-700" data-testid={`mrp-shortfall-${i}`}>{r.net_shortfall.toLocaleString()}</span>
                          ) : (
                            <span className="inline-flex px-2 py-0.5 rounded-full bg-green-100 text-green-700">0</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="px-4 py-2 border-t border-gray-50 text-[10px] text-gray-400">
                Net Shortfall = (Required + In Planned + In Progress) − (In Stock + Ordered). Positive = quantity to procure.
              </div>
            </div>
          )}
        </div>
      )}

      {/* ==================== UNIT CONVERTER TAB ==================== */}
      {activeTab === 'converter' && (
        <div className="space-y-5">
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <h3 className="text-sm font-bold text-gray-900 mb-1" style={{ fontFamily: 'DM Sans' }}>Unit Conversion Matrix</h3>
            <p className="text-xs text-gray-500 mb-4">Convert between purchase, production, and sales units</p>
            <div className="grid md:grid-cols-5 gap-3 items-end">
              <div>
                <label className="block text-[11px] font-medium text-gray-700 mb-1">Product / Material</label>
                <input data-testid="conv-product" value={convForm.product_name} onChange={e => setConvForm({ ...convForm, product_name: e.target.value })} placeholder="e.g., 25x25x3 MS Angle" className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
              </div>
              <div>
                <label className="block text-[11px] font-medium text-gray-700 mb-1">Value</label>
                <input data-testid="conv-value" type="number" value={convForm.value} onChange={e => setConvForm({ ...convForm, value: Number(e.target.value) })} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
              </div>
              <div>
                <label className="block text-[11px] font-medium text-gray-700 mb-1">From Unit</label>
                <select data-testid="conv-from" value={convForm.from_unit} onChange={e => setConvForm({ ...convForm, from_unit: e.target.value })} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200">
                  {['pcs', 'kg', 'meters', 'feet', 'sqft', 'sqm', 'MT', 'rolls'].map(u => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-medium text-gray-700 mb-1">To Unit</label>
                <select data-testid="conv-to" value={convForm.to_unit} onChange={e => setConvForm({ ...convForm, to_unit: e.target.value })} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200">
                  {['pcs', 'kg', 'meters', 'feet', 'sqft', 'sqm', 'MT', 'rolls'].map(u => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
              <button data-testid="conv-btn" onClick={handleConvert} disabled={convLoading} className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white rounded-lg hover:shadow-lg disabled:opacity-50" style={{ backgroundColor: '#24549c' }}>
                {convLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowRightLeft className="w-3.5 h-3.5" />}Convert
              </button>
            </div>
            {convResult && !convResult.error && (
              <div className="mt-4 p-4 bg-blue-50 border border-blue-100 rounded-xl">
                <div className="flex items-center gap-4">
                  <div className="text-center"><p className="text-xl font-bold" style={{ color: '#24549c' }}>{convForm.value}</p><p className="text-[10px] text-gray-500">{convResult.from_unit}</p></div>
                  <ArrowRightLeft className="w-5 h-5" style={{ color: '#24549c' }} />
                  <div className="text-center"><p className="text-xl font-bold" style={{ color: '#24549c' }}>{convResult.result}</p><p className="text-[10px] text-gray-500">{convResult.to_unit}</p></div>
                </div>
                <p className="text-[10px] text-gray-500 mt-2">Product: {convResult.product} | Factor: 1 {convResult.from_unit} = {convResult.factor} {convResult.to_unit}</p>
              </div>
            )}
            {convResult?.error && (<div className="mt-4 p-3 bg-red-50 border border-red-100 rounded-lg text-xs text-red-700">{convResult.error}</div>)}
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <div className="bg-white rounded-xl border border-gray-100 p-4">
              <h4 className="text-xs font-bold text-gray-900 mb-3" style={{ fontFamily: 'DM Sans' }}>Fabrication Materials - Quick Reference</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead><tr className="bg-gray-50"><th className="text-left px-3 py-2 text-[10px] text-gray-500">Item</th><th className="text-right px-3 py-2 text-[10px] text-gray-500">1 Piece</th><th className="text-right px-3 py-2 text-[10px] text-gray-500">Weight</th></tr></thead>
                  <tbody>
                    {[{ item: '25x25x3mm MS Angle', length: '6 meters', weight: '9 kg' },{ item: '40x40x3mm MS Angle', length: '6 meters', weight: '14 kg' },{ item: '50x50x5mm MS Angle', length: '6 meters', weight: '22 kg' },{ item: '76 OD Pipe (Medium)', length: '6 meters', weight: '18 kg' },{ item: '40 OD Pipe (Medium)', length: '6 meters', weight: '8 kg' },{ item: '50x25mm MS Channel', length: '6 meters', weight: '16 kg' }].map((r, i) => (
                      <tr key={i} className="border-t border-gray-50"><td className="px-3 py-2 font-medium">{r.item}</td><td className="px-3 py-2 text-right text-gray-500">{r.length}</td><td className="px-3 py-2 text-right font-semibold">{r.weight}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-4">
              <h4 className="text-xs font-bold text-gray-900 mb-3" style={{ fontFamily: 'DM Sans' }}>ACP / Signage Sheets - Standard Sizes</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead><tr className="bg-gray-50"><th className="text-left px-3 py-2 text-[10px] text-gray-500">Size (ft)</th><th className="text-right px-3 py-2 text-[10px] text-gray-500">Area (sqft)</th><th className="text-right px-3 py-2 text-[10px] text-gray-500">Area (sqm)</th></tr></thead>
                  <tbody>
                    {[{ size: '7 x 4', sqft: 28, sqm: 2.6 },{ size: '8 x 4', sqft: 32, sqm: 2.97 },{ size: '10 x 4', sqft: 40, sqm: 3.72 },{ size: '12 x 4', sqft: 48, sqm: 4.46 }].map((r, i) => (
                      <tr key={i} className="border-t border-gray-50"><td className="px-3 py-2 font-medium">{r.size}</td><td className="px-3 py-2 text-right font-semibold">{r.sqft}</td><td className="px-3 py-2 text-right text-gray-500">{r.sqm}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ==================== ERP OVERVIEW TAB ==================== */}
      {activeTab === 'dashboard' && erpData && (
        <div className="space-y-5">
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
            {[
              { l: 'Products', v: erpData.summary?.total_products || 0, c: '#24549c' },
              { l: 'Purchase Orders', v: erpData.summary?.total_pos || 0, c: '#3a6fc4' },
              { l: 'Stock Items', v: erpData.summary?.total_stock_items || 0, c: '#2E7D32' },
              { l: 'Production', v: erpData.summary?.total_production || 0, c: '#E65100' },
              { l: 'Dispatches', v: erpData.summary?.total_dispatches || 0, c: '#6A1B9A' },
              { l: 'Indents', v: erpData.summary?.total_indents || 0, c: '#BF360C' },
            ].map((s, i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-100 p-3 text-center">
                <p className="text-lg font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>{s.v}</p>
                <p className="text-[10px] text-gray-500 flex items-center justify-center gap-1"><span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: s.c }} />{s.l}</p>
              </div>
            ))}
          </div>
          {erpData.stock?.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100"><h3 className="text-xs font-bold text-gray-900">Current Stock Levels</h3></div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead><tr className="bg-gray-50">{['Product', 'Batch', 'Qty', 'Unit', 'Value', 'Status', 'Reorder Level'].map(h => <th key={h} className="text-left px-4 py-2 text-[10px] font-semibold text-gray-500">{h}</th>)}</tr></thead>
                  <tbody>{erpData.stock.map((s, i) => (
                    <tr key={i} className="border-t border-gray-50">
                      <td className="px-4 py-2 font-semibold">{s.product}</td>
                      <td className="px-4 py-2 font-mono text-gray-500">{s.batch}</td>
                      <td className="px-4 py-2 font-bold">{s.qty?.toLocaleString()}</td>
                      <td className="px-4 py-2 text-gray-500">{s.unit}</td>
                      <td className="px-4 py-2">INR {(s.value || 0).toLocaleString()}</td>
                      <td className="px-4 py-2"><span className={`inline-flex px-1.5 py-0.5 text-[9px] font-bold rounded-full ${s.status === 'In Stock' ? 'bg-green-100 text-green-700' : s.status === 'Low Stock' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>{s.status}</span></td>
                      <td className="px-4 py-2 text-gray-400">{s.reorder_level}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            </div>
          )}
          {erpData.purchase_orders?.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100"><h3 className="text-xs font-bold text-gray-900">Recent Purchase Orders</h3></div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead><tr className="bg-gray-50">{['PO #', 'Vendor', 'Type', 'Amount', 'Status', 'Delivery', 'Items'].map(h => <th key={h} className="text-left px-4 py-2 text-[10px] font-semibold text-gray-500">{h}</th>)}</tr></thead>
                  <tbody>{erpData.purchase_orders.slice(0, 10).map((po, i) => (
                    <tr key={i} className="border-t border-gray-50">
                      <td className="px-4 py-2 font-mono font-semibold">{po.po_number?.substring(0, 16)}</td>
                      <td className="px-4 py-2">{po.vendor}</td>
                      <td className="px-4 py-2"><span className={`inline-flex px-1.5 py-0.5 text-[9px] font-bold rounded-full ${po.type === 'domestic' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>{po.type}</span></td>
                      <td className="px-4 py-2 font-semibold">INR {(po.amount || 0).toLocaleString()}</td>
                      <td className="px-4 py-2"><span className={`inline-flex px-1.5 py-0.5 text-[9px] font-bold rounded-full ${po.status === 'Ordered' ? 'bg-amber-100 text-amber-700' : po.status === 'Received' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>{po.status}</span></td>
                      <td className="px-4 py-2 text-gray-500">{po.delivery}</td>
                      <td className="px-4 py-2">{po.items?.length || 0} items</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
      {activeTab === 'dashboard' && !erpData && (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /><span className="ml-2 text-sm text-gray-500">Loading ERP data...</span></div>
      )}
    </div>
  );
};

export default ForecastModule;
